import { useState } from "react";
import SectionTitle from "./common/SectionTitle";
import { CLINICS } from "../constants/data";
import { scrollTo } from "../utils/scroll";
import { DOCTOR_CONTACT, sendAppointmentForm } from "../utils/contact";

export default function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    clinic: "0",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [whatsappUrls, setWhatsappUrls] = useState<{
    doctor: string;
    clinic: string;
  } | null>(null);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.phone) {
      alert("Please fill in all required fields");
      return;
    }

    setIsLoading(true);

    try {
      const result = await sendAppointmentForm(formData);

      if (result.success && result.whatsappUrl) {
        setWhatsappUrls({
          doctor: result.whatsappUrl,
          clinic: result.clinicWhatsappUrl || result.whatsappUrl,
        });
        setSubmitted(true);

        // Reset form after 2 seconds
        setTimeout(() => {
          setFormData({ name: "", phone: "", clinic: "0", message: "" });
        }, 2000);

        // Auto-clear messages after 8 seconds
        setTimeout(() => {
          setSubmitted(false);
          setWhatsappUrls(null);
        }, 8000);
      } else {
        alert(result.error || "Failed to process appointment request");
      }
    } catch (error) {
      console.error("Error:", error);
      alert("An error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      id="contact"
      style={{
        padding: "80px 20px",
        background: "linear-gradient(135deg,#f8faff,#eff6ff)",
      }}
    >
      <div style={{ maxWidth: 1000, margin: "0 auto" }}>
        <SectionTitle
          tag="Get In Touch"
          title="Book an Appointment"
          sub="Reach out to schedule a consultation at either of our clinic locations"
        />

        <div
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 36 }}
        >
          <div
            style={{
              background: "white",
              borderRadius: 28,
              padding: "36px 32px",
              boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
              border: "1px solid #e2e8f0",
            }}
          >
            <h3
              style={{
                fontWeight: 700,
                fontSize: 18,
                color: "#1e293b",
                marginBottom: 24,
              }}
            >
              Send a Message
            </h3>

            {submitted && whatsappUrls && (
              <div
                style={{
                  background: "#f0fdf9",
                  border: "1px solid #a7f3d0",
                  color: "#065f46",
                  borderRadius: 14,
                  padding: "16px 18px",
                  marginBottom: 20,
                  fontSize: 14,
                }}
              >
                <div style={{ fontWeight: 600, marginBottom: 12 }}>
                  ✅ Appointment request ready! Send it via WhatsApp:
                </div>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  <a
                    href={whatsappUrls.doctor}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      background: "#059669",
                      color: "white",
                      padding: "8px 16px",
                      borderRadius: 8,
                      textDecoration: "none",
                      fontWeight: 600,
                      fontSize: 12,
                      cursor: "pointer",
                      transition: "opacity 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLAnchorElement).style.opacity = "0.9";
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLAnchorElement).style.opacity = "1";
                    }}
                  >
                    📱 Send to Doctor
                  </a>
                  <a
                    href={whatsappUrls.clinic}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      background: "#059669",
                      color: "white",
                      padding: "8px 16px",
                      borderRadius: 8,
                      textDecoration: "none",
                      fontWeight: 600,
                      fontSize: 12,
                      cursor: "pointer",
                      transition: "opacity 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLAnchorElement).style.opacity = "0.9";
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLAnchorElement).style.opacity = "1";
                    }}
                  >
                    📱 Send to Clinic
                  </a>
                </div>
              </div>
            )}

            <div style={{ marginBottom: 18 }}>
              <label
                style={{
                  display: "block",
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#475569",
                  marginBottom: 6,
                }}
              >
                Full Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Your full name"
                disabled={isLoading}
                style={{
                  width: "100%",
                  border: "1.5px solid #e2e8f0",
                  borderRadius: 14,
                  padding: "12px 16px",
                  fontSize: 14,
                  outline: "none",
                  boxSizing: "border-box",
                  transition: "border 0.2s",
                  opacity: isLoading ? 0.6 : 1,
                }}
                onFocus={(e) => {
                  e.currentTarget.style.border = "1.5px solid #1d4ed8";
                }}
                onBlur={(e) => {
                  e.currentTarget.style.border = "1.5px solid #e2e8f0";
                }}
              />
            </div>

            <div style={{ marginBottom: 18 }}>
              <label
                style={{
                  display: "block",
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#475569",
                  marginBottom: 6,
                }}
              >
                Phone Number *
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+91 XXXXX XXXXX"
                disabled={isLoading}
                style={{
                  width: "100%",
                  border: "1.5px solid #e2e8f0",
                  borderRadius: 14,
                  padding: "12px 16px",
                  fontSize: 14,
                  outline: "none",
                  boxSizing: "border-box",
                  transition: "border 0.2s",
                  opacity: isLoading ? 0.6 : 1,
                }}
                onFocus={(e) => {
                  e.currentTarget.style.border = "1.5px solid #1d4ed8";
                }}
                onBlur={(e) => {
                  e.currentTarget.style.border = "1.5px solid #e2e8f0";
                }}
              />
            </div>

            <div style={{ marginBottom: 18 }}>
              <label
                style={{
                  display: "block",
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#475569",
                  marginBottom: 6,
                }}
              >
                Preferred Clinic
              </label>
              <select
                name="clinic"
                value={formData.clinic}
                onChange={handleChange}
                disabled={isLoading}
                style={{
                  width: "100%",
                  border: "1.5px solid #e2e8f0",
                  borderRadius: 14,
                  padding: "12px 16px",
                  fontSize: 14,
                  outline: "none",
                  boxSizing: "border-box",
                  background: "white",
                  color: "#475569",
                  opacity: isLoading ? 0.6 : 1,
                }}
              >
                <option value="0">Ashtamangal Surgical Hospital – Shahibaug</option>
                <option value="1">Invictus Gastro Hospital – Nikol</option>
              </select>
            </div>

            <div style={{ marginBottom: 24 }}>
              <label
                style={{
                  display: "block",
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#475569",
                  marginBottom: 6,
                }}
              >
                Message
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={4}
                placeholder="Briefly describe your condition or query..."
                disabled={isLoading}
                style={{
                  width: "100%",
                  border: "1.5px solid #e2e8f0",
                  borderRadius: 14,
                  padding: "12px 16px",
                  fontSize: 14,
                  outline: "none",
                  boxSizing: "border-box",
                  resize: "none",
                  transition: "border 0.2s",
                  fontFamily: "inherit",
                  opacity: isLoading ? 0.6 : 1,
                }}
                onFocus={(e) => {
                  e.currentTarget.style.border = "1.5px solid #1d4ed8";
                }}
                onBlur={(e) => {
                  e.currentTarget.style.border = "1.5px solid #e2e8f0";
                }}
              />
            </div>

            <button
              onClick={handleSubmit}
              disabled={isLoading}
              style={{
                width: "100%",
                background: "linear-gradient(135deg,#1d4ed8,#0d9488)",
                color: "white",
                border: "none",
                fontWeight: 700,
                fontSize: 15,
                padding: "14px",
                borderRadius: 14,
                cursor: isLoading ? "not-allowed" : "pointer",
                boxShadow: "0 4px 16px rgba(29,78,216,0.3)",
                transition: "opacity 0.2s",
                opacity: isLoading ? 0.7 : 1,
              }}
              onMouseEnter={(e) => {
                if (!isLoading) {
                  (e.currentTarget as HTMLButtonElement).style.opacity = "0.9";
                }
              }}
              onMouseLeave={(e) => {
                if (!isLoading) {
                  (e.currentTarget as HTMLButtonElement).style.opacity = "1";
                }
              }}
            >
              {isLoading ? "Processing..." : "Send Message →"}
            </button>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            {CLINICS.map((clinic, i) => (
              <div
                key={i}
                style={{
                  background: "white",
                  borderRadius: 22,
                  padding: "24px",
                  border: "1px solid #e2e8f0",
                  boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
                }}
              >
                <div
                  style={{
                    fontWeight: 700,
                    color: "#1e293b",
                    marginBottom: 8,
                    fontSize: 15,
                  }}
                >
                  {clinic.name}
                </div>
                <p
                  style={{
                    color: "#64748b",
                    fontSize: 13,
                    marginBottom: 6,
                    lineHeight: 1.6,
                  }}
                >
                  📍 {clinic.address}
                </p>
                <p style={{ color: "#64748b", fontSize: 13 }}>
                  📞 {clinic.phone}
                </p>
              </div>
            ))}

            <div
              style={{
                background: "linear-gradient(135deg,#059669,#0d9488)",
                borderRadius: 22,
                padding: "28px",
                color: "white",
              }}
            >
              <div style={{ fontSize: 32, marginBottom: 8 }}>💬</div>
              <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 6 }}>
                Direct WhatsApp
              </div>
              <p
                style={{
                  color: "rgba(255,255,255,0.8)",
                  fontSize: 13,
                  marginBottom: 16,
                  lineHeight: 1.6,
                }}
              >
                Chat directly with {DOCTOR_CONTACT.name}
              </p>
              <a
                href={`https://wa.me/${DOCTOR_CONTACT.phone.replace(/\s/g, "")}?text=Hello%20Dr.%20Modi%2C%20I%20would%20like%20to%20book%20an%20appointment.`}
                target="_blank"
                rel="noreferrer"
                style={{
                  display: "inline-block",
                  background: "white",
                  color: "#059669",
                  fontWeight: 700,
                  fontSize: 13,
                  padding: "10px 22px",
                  borderRadius: 50,
                  textDecoration: "none",
                  transition: "opacity 0.2s",
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLAnchorElement).style.opacity = "0.9";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLAnchorElement).style.opacity = "1";
                }}
              >
                Chat on WhatsApp →
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
